import { useTranslation } from 'react-i18next';

import { formatInstant } from '@/features/inventory/registerUi';
import type { CommissionedDevice } from '@/features/commissioning/useCommissioning';
import type { CommandClearance } from './useClearance';

/**
 * SLICE 14c — the command-clearance cell on the bench.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 TWO DISTINCT STATES, TWO SETS OF WORDS, NEVER ONE GREEN TICK.
 * ─────────────────────────────────────────────────────────────────────────────
 * This cell sits BESIDE the 14a "SMS lock" column, not on top of it: hardening
 * and clearance are two orthogonal facts and the screen keeps them physically
 * apart. Here it renders the answer to "why is this device commandable, if it is"
 * and offers the one control that changes it.
 *
 *   HARDENED   → "Command proven", in `raad-primary`. A machine proof; there is
 *                nothing to clear or withdraw, so no button. This must read as a
 *                different thing, in different words, from an attestation.
 *   CLEARED    → "Cleared {date}", in `raad-warn`, with the note beneath it and a
 *                Withdraw action. A temporary, accepted-risk state — warn tone, not
 *                the healthy primary the proof gets.
 *   NOT CLEARED→ a command-capable tracker that is neither proven nor cleared,
 *                with a "Clear for commanding" action.
 *   NO CHANNEL → an app-based tracker (`no_command_channel`). It can never be
 *                commanded, so it reads as a quiet muted line, not a warning — the
 *                same reasoning the 14a column uses for its dash.
 *
 * `not_commissioned` never appears on the bench (every row here is commissioned),
 * but it is handled as "no channel to act on" for completeness rather than left to
 * fall through to a clear button that would 404/409.
 */
export function ClearanceCell({
  hardening,
  clearance,
  language,
  onClear,
  onWithdraw,
}: {
  hardening: CommissionedDevice['hardening'];
  /** The device's row in `GET /admin/command-clearances`, or undefined when it is
   *  not cleared-but-not-hardened. A hardened device is never in that list. */
  clearance: CommandClearance | undefined;
  language: string;
  onClear: () => void;
  onWithdraw: () => void;
}) {
  const { t } = useTranslation();

  if (hardening === 'hardened') {
    // PROVEN. A machine proof, worded and coloured unlike any attestation.
    return (
      <span className="block">
        <span className="font-semibold text-raad-primary">{t('clearance.state.hardened')}</span>
        <span className="mt-0.5 block text-[11px] text-raad-muted">
          {t('clearance.state.hardenedHint')}
        </span>
      </span>
    );
  }

  if (hardening === 'no_command_channel' || hardening === 'not_commissioned') {
    // Nothing to command and nothing to clear. Quiet, not a warning.
    return <span className="text-raad-muted">{t('clearance.state.noChannel')}</span>;
  }

  if (clearance !== undefined) {
    // ATTESTED. Temporary, accepted-risk — warn tone, with what was done and by
    // when, and the way to take it back.
    const when =
      clearance.commandEnabledAt !== null
        ? formatInstant(clearance.commandEnabledAt, language)
        : '';
    return (
      <span className="block">
        <span className="font-semibold text-raad-warn">
          {/* 🔴 BY WHOM, not just when. A clearance is one named person accepting
              a risk on a customer's vehicle; a row that says only "Cleared 9 Sep"
              is an unsigned decision, and the first question anyone asks of it is
              the one it cannot answer. Falls back to the date-only string if the
              wire ever omits the name, rather than rendering "Cleared by ". */}
          {clearance.commandEnabledBy
            ? t('clearance.state.clearedBy', { who: clearance.commandEnabledBy, date: when })
            : t('clearance.state.cleared', { date: when })}
        </span>
        {clearance.commandEnabledNote ? (
          <span className="mt-0.5 block text-[11px] text-raad-muted">
            {clearance.commandEnabledNote}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onWithdraw}
          className="mt-1 min-h-8 rounded-lg border border-raad-border px-2.5 text-[12px] font-semibold transition-colors hover:bg-raad-primary/10"
        >
          {t('clearance.withdraw.action')}
        </button>
      </span>
    );
  }

  // Command-capable, neither proven nor cleared.
  return (
    <span className="block">
      <span className="text-raad-muted">{t('clearance.state.notCleared')}</span>
      <button
        type="button"
        onClick={onClear}
        className="mt-1 block min-h-8 rounded-lg border border-raad-border px-2.5 text-[12px] font-semibold transition-colors hover:bg-raad-primary/10"
      >
        {t('clearance.clear.action')}
      </button>
    </span>
  );
}
